import React, { useState, useEffect, useCallback } from "react";
import { dataGridSurface } from "../common/tableStyles";
import { Avatar, Box, Chip, IconButton, Tooltip, styled, Snackbar, Alert, Rating } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StorefrontIcon from "@mui/icons-material/Storefront";
import IShop from "../../entities/IShop";
import ShopService from "../../services/ShopService";
import { unwrapList, getErrorMessage } from "../../services/apiUtils";
import { resolveShopActive, shopStatusLabel } from "./shopUtils";
import DataGridLoadingOverlay from "../common/DataGridLoadingOverlay";
import ConfirmDialog from "../common/ConfirmDialog";
import { theme } from "../../theme";


const StyledTable = styled(Box)(({ theme }) => ({
  ...dataGridSurface(theme),
  margin: "20px 0 0 0",
  height: 560,
}));

type ShopTableProps = {
  onEdit: (shopId: number) => void;
  reloadToken?: number;
};

const ShopTable: React.FC<ShopTableProps> = ({ onEdit, reloadToken }) => {
  const [shops, setShops] = useState<Array<IShop>>([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState<IShop | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const loadShops = useCallback(() => {
    setLoading(true);
    ShopService.getShops()
      .then((res: any) => {
        setShops(unwrapList<IShop>(res));
      })
      .catch((err: any) => {
        setShops([]);
        setSnackbar({ open: true, message: getErrorMessage(err, "Could not load shops."), severity: "error" });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadShops();
  }, [loadShops, reloadToken]);

  const handleDelete = () => {
    if (!confirmTarget) return;
    setDeleting(true);
    ShopService.deleteShop(confirmTarget.shopId)
      .then(() => {
        setSnackbar({ open: true, message: "Shop deleted.", severity: "success" });
        setConfirmTarget(null);
        loadShops();
      })
      .catch((err: any) => {
        setSnackbar({ open: true, message: getErrorMessage(err, "Could not delete this shop."), severity: "error" });
      })
      .finally(() => setDeleting(false));
  };

  const columns: GridColDef[] = [
    { field: "shopId", headerName: "ID", flex: 0.35 },
    {
      field: "profileImage",
      headerName: "",
      flex: 0.35,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar src={params.row.profileImage || undefined} sx={{ width: 32, height: 32 }}>
          <StorefrontIcon fontSize="small" />
        </Avatar>
      ),
    },
    { field: "shopName", headerName: "Shop Name", flex: 1.1 },
    { field: "shopNumber", headerName: "Shop Number", flex: 0.9 },
    { field: "customerName", headerName: "Customer", flex: 1 },
    { field: "customerId", headerName: "Customer ID", flex: 0.6 },
    {
      field: "rating",
      headerName: "Rating",
      flex: 0.9,
      renderCell: (params) => (
        <Rating value={Number(params.row.rating) || 0} precision={0.5} size="small" readOnly />
      ),
    },
    { field: "followers", headerName: "Followers", flex: 0.6 },
    { field: "sold", headerName: "Sold", flex: 0.5 },
    { field: "items", headerName: "Items", flex: 0.5 },
    {
      field: "shopStatus",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          size="small"
          label={shopStatusLabel(params.row)}
          color={resolveShopActive(params.row) ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit shop details">
            <IconButton
              sx={{ color: theme.palette.primary.main }}
              onClick={() => onEdit(params.row.shopId)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete shop">
            <IconButton sx={{ color: theme.status.danger }} onClick={() => setConfirmTarget(params.row)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <StyledTable>
      <DataGrid
        sx={{ backgroundColor: "transparent" }}
        rows={shops}
        columns={columns}
        getRowId={(row: any) => row.shopId}
        components={{ LoadingOverlay: DataGridLoadingOverlay }}
        loading={loading}
        pageSize={20}
        rowsPerPageOptions={[20, 50]}
        disableSelectionOnClick
      />

      <ConfirmDialog
        open={!!confirmTarget}
        title="Delete Shop"
        message={`Are you sure you want to delete "${confirmTarget?.shopName}"? This cannot be undone.`}
        loading={deleting}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={handleDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledTable>
  );
};

export default ShopTable;
